import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';

import type { FilterConfig } from '../types';
import { DataTableFilterModel } from '../models/DataTableFilterModel';
import { FilterDisplayValue } from '../models/FilterDisplayValue';

import { FilterType } from '../types/FilterType';
import { FILTER_TYPES } from '../constants/FilterTypes';

// @ts-ignore
import filterAltIcon from '../../../assets/images/datatable/filter-alt.svg';
// @ts-ignore
import filterAltOffIcon from '../../../assets/images/datatable/filter-alt-off2.svg';
// @ts-ignore
import sortAscIcon from '../../../assets/images/datatable/sort-asc.svg';
// @ts-ignore
import sortDescIcon from '../../../assets/images/datatable/sort-desc.svg';
// @ts-ignore
import noSortIcon from '../../../assets/images/datatable/no-sort.svg';

interface Props {
    config: FilterConfig;
    currentFilters: Record<string, any>;
    sortField?: string;
    sortOrder?: number;
    onFilterChange: (fieldName: string, type: FilterType, value: any) => void;
    onFilterClear: (fieldName: string) => void;
    onFilterClick: (
        event: React.MouseEvent | React.KeyboardEvent | React.FocusEvent,
        fieldName: string,
        type: FilterType,
        params: any
    ) => void;
    onSortToggle: (fieldName: string) => void;
    label: string;
}

const getDisplayValue = (value: unknown): string => FilterDisplayValue.toText(value, FilterDisplayValue.HEADER_KEYS);

export const InlineFilterHeader: React.FC<Props> = ({
    config,
    currentFilters,
    sortField,
    sortOrder,
    onFilterChange,
    onFilterClear,
    onFilterClick,
    onSortToggle,
    label,
}) => {
    const { t } = useTranslation();

    const currentFilter = currentFilters[config.fieldName];
    const currentValue = currentFilter?.value;
    const serializedCurrentValue = JSON.stringify(currentValue ?? null);

    const [localValue, setLocalValue] = useState<any>(() =>
        config.type === FILTER_TYPES.LIST
            ? currentValue?.[0] ?? null
            : getDisplayValue(currentValue),
    );

    useEffect(() => {
        if (config.type === FILTER_TYPES.LIST) {
            setLocalValue(currentValue?.[0] ?? null);
        } else {
            setLocalValue(getDisplayValue(currentValue));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.type, serializedCurrentValue]);

    const isActive = DataTableFilterModel.isActive(currentValue);
    const isThisSorted = sortField === config.fieldName;

    const filterIcon = isActive ? filterAltOffIcon : filterAltIcon;

    const sortIcon =
        isThisSorted && sortOrder === 1
            ? sortAscIcon
            : isThisSorted && sortOrder === -1
                ? sortDescIcon
                : noSortIcon;

    const clearLocalValue = () => {
        if (config.type === FILTER_TYPES.LIST) {
            setLocalValue(null);
            return;
        }

        if (
            config.type === FILTER_TYPES.DATE_RANGE ||
            config.type === FILTER_TYPES.DATE_TIME_RANGE ||
            config.type === FILTER_TYPES.NUMBER_RANGE ||
            config.type === FILTER_TYPES.AMOUNT
        ) {
            setLocalValue({ from: null, to: null });
            return;
        }

        setLocalValue('');
    };

   const handleFilterButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation?.();

        if (isActive) {
            clearLocalValue();
            onFilterClear(config.fieldName);
            return;
        }

        onFilterClick(event, config.fieldName, config.type, config.params);
    };

    const renderFilterButton = () => (
        <button
            type="button"
            className={`dt2-filter-btn ${isActive ? 'dt2-filter-btn--active' : ''}`}
            onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={handleFilterButtonClick}
            title={
                isActive
                    ? t('dataTable.removeFilter', 'Remove filter')
                    : t('dataTable.openFilter', 'Open filter')
            }
        >
            <img src={filterIcon} alt="" />
        </button>
    );

    const renderInput = () => {
        switch (config.type) {
            case FILTER_TYPES.TEXT:
                return (
                    <div className="dt2-filter-row">
                        <InputText
                            className="dt2-inline-input"
                            value={localValue ?? ''}
                            placeholder={label}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                onFilterClick(event, config.fieldName, config.type, localValue);
                            }}
                            onChange={e => {
                                setLocalValue(e.target.value);
                                onFilterChange(config.fieldName, config.type, e.target.value);
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onFilterClick(event, config.fieldName, config.type, localValue);
                                }
                            }}
                        />

                        {renderFilterButton()}
                    </div>
                );

            case FILTER_TYPES.LIST: {
                const options = config.params ?? [];

                return (
                    <div className="dt2-filter-row dt2-filter-row--list">
                        <div
                            className="p-dropdown p-component p-inputwrapper dt2-inline-select"
                            role="button"
                            tabIndex={0}
                            onMouseDown={event => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                onFilterClick(event, config.fieldName, config.type, options);
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onFilterClick(event, config.fieldName, config.type, options);
                                }
                            }}
                        >
                            <span
                                className={`p-dropdown-label ${
                                    getDisplayValue(currentValue) ? '' : 'p-placeholder'
                                }`}
                            >
                                {getDisplayValue(currentValue) || t('common.select', 'Select')}
                            </span>

                            <div className="p-dropdown-trigger">
                                <span className="p-dropdown-trigger-icon pi pi-chevron-down" />
                            </div>
                        </div>

                        {renderFilterButton()}
                    </div>
                );
            }

            case FILTER_TYPES.DATE_RANGE:
            case FILTER_TYPES.DATE_TIME_RANGE: {
                const isDateTime = config.type === FILTER_TYPES.DATE_TIME_RANGE;

                return (
                    <div className="dt2-filter-row">
                        <Calendar
                            className="dt2-inline-calendar"
                            inputClassName="dt2-calendar-input"
                            value={
                                localValue?.from || localValue?.to
                                    ? [
                                        localValue?.from ? new Date(localValue.from) : null,
                                        localValue?.to ? new Date(localValue.to) : null,
                                    ]
                                    : null
                            }
                            onChange={e => {
                                const [from, to] = (e.value as Date[] | null) ?? [null, null];

                                const next = {
                                    from: from ?? null,
                                    to: to ?? null,
                                };

                                setLocalValue(next);
                                onFilterChange(config.fieldName, config.type, next);
                            }}
                            selectionMode="range"
                            showTime={isDateTime}
                            hourFormat="24"
                            placeholder={label}
                            readOnlyInput
                            hideOnRangeSelection
                            showIcon={false}
                            dateFormat="yy-mm-dd"
                            panelClassName="dt2-calendar-panel"
                        />

                        {renderFilterButton()}
                    </div>
                );
            }

            case FILTER_TYPES.NUMBER_RANGE:
            case FILTER_TYPES.AMOUNT: {
                const singleValue =
                    localValue?.from != null && localValue?.from === localValue?.to
                        ? localValue.from
                        : '';

                return (
                    <div className="dt2-filter-row">
                        <InputText
                            className="dt2-inline-input"
                            value={singleValue}
                            placeholder={label}
                            keyfilter="num"
                            onChange={e => {
                                const value = e.target.value;

                                const next =
                                    value === ''
                                        ? { from: null, to: null }
                                        : {
                                            from: Number(value),
                                            to: Number(value),
                                        };

                                setLocalValue(next);
                                onFilterChange(config.fieldName, config.type, next);
                            }}
                        />

                        {renderFilterButton()}
                    </div>
                );
            }
            case FILTER_TYPES.BOOLEAN:
                return (
                    <div className="dt2-filter-row dt2-filter-row--list">
                        <Dropdown
                            className="dt2-inline-select"
                            panelClassName="dt2-inline-select-panel"
                            value={localValue}
                            options={[
                                { id: true, label: t('Boolean.true') },
                                { id: false, label: t('Boolean.false') },
                            ]}
                            optionLabel="label"
                            optionValue="id"
                            placeholder={t('common.select', 'Select')}
                            showClear={false}
                            onChange={e => {
                                setLocalValue(e.value);
                                onFilterChange(config.fieldName, config.type, e.value);
                            }}
                        />

                        {renderFilterButton()}
                    </div>
                );

            default:
                return (
                    <div className="dt2-filter-row">
                        <div className="dt2-select-placeholder">
                            {t('common.select', 'Select')}
                            <i className="pi pi-chevron-down" />
                        </div>

                        {renderFilterButton()}
                    </div>
                );
        }
    };

    const isHeaderHighlighted = isActive || isThisSorted;

    return (
        <div className={`dt2-col-header ${isHeaderHighlighted ? 'dt2-col-header--highlighted' : ''}`}>
            <div className="dt2-col-header__title-row">
                <span>{label}</span>

                <button
                    type="button"
                    className="dt2-sort-btn"
                    onClick={() => onSortToggle(config.fieldName)}
                    title={t('dataTable.sort', 'Sort')}
                >
                    <img src={sortIcon} alt="" />
                </button>
            </div>

            {renderInput()}
        </div>
    );
};